// useCanisterHealth.ts — Fetches the cycles dashboard.
//
// Switched 2026-05-22 (post-launch) from admin_get_all_canister_health() to
// get_all_canister_cycles() because:
//   1) The legacy method covered only 4 of 7 canisters (no stats_canister,
//      no dominus_admin self-report, no dominus_frontend).
//   2) Each backend's `admin_get_canister_status` query always returns
//      heapBytes / heapPercent / memoryBytes / memoryPercent = 0 because
//      the Motoko stdlib does not expose RTS heap stats. The "vital signs"
//      cards therefore showed misleading 0% bars.
//   3) The IDL declared the rollup field as `label_` (Motoko source name)
//      but the wire emits `label` (the Motoko `_` escape collapses on the
//      Candid boundary). The decode threw "Cannot find required field
//      label_" on every poll. (Fixed in adminAgent.ts in the same commit;
//      legacy method left wired but unused.)
//
// New shape: call `get_all_canister_cycles()` (live IC.canister_status cycle
// reads per canister, 7-entry coverage) and `get_backend_principals()` (so
// the inspector panel still has each canister's principal). Join by label.
//
// Heap and memory continue to surface as 0 here — the UI now renders an
// em-dash for those rows (see OperationsPage CanisterCard). Real heap /
// memory monitoring is a v1.1 follow-up (requires Motoko RTS bindings that
// dfx 0.25 / moc 0.13 do not yet expose).
//
// Auto-refreshes every 60s. Used by the Operations section (Section III).

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Identity } from '@dfinity/agent';
import { Principal } from '@dfinity/principal';
import {
  createAdminAgent,
  createAdminActor,
  type CanisterRollup,
  type CanisterHealthStatus,
  type BackendPrincipals,
  type CanisterCyclesEntry,
} from '../lib/adminAgent';

const POLL_INTERVAL_MS = 60_000;

export interface UseCanisterHealthReturn {
  rollups: CanisterRollup[];
  /// Cycle balance of dominus_admin itself — the pool that Auto-Top-Up and
  /// Manual Top-Up draw from when distributing to the other 6 canisters.
  /// Derived from the "dominus_admin" entry in get_all_canister_cycles.
  /// Null until the first successful fetch (distinct from 0n: unknown vs. empty).
  treasuryCycles: bigint | null;
  /// Sum of cycles across every entry in the rollup (all 7 canisters incl. treasury).
  /// Surfaces the operational decision-relevant aggregate at a glance.
  totalCycles: bigint | null;
  isLoading: boolean;
  error: string | null;
  lastFetchedAt: Date | null;
  refresh: () => void;
}

// Sentinel principal used when a cycles-list entry has no matching principal
// in the backend directory. The inspector skips any entry whose principal
// equals this sentinel (it would otherwise return 422 from the IC Dashboard,
// since aaaaa-aa is the management canister and not a user-deployed canister).
const SENTINEL_PRINCIPAL = Principal.fromText('aaaaa-aa');

// dominus_admin's own principal comes from the Vite-injected env var since the
// canister cannot return its own principal via get_backend_principals (that
// directory is for the *other* canisters dominus_admin tops up).
const ADMIN_CANISTER_ID = import.meta.env.VITE_ADMIN_CANISTER_ID as string | undefined;
const ADMIN_PRINCIPAL: Principal | null = (() => {
  if (!ADMIN_CANISTER_ID || ADMIN_CANISTER_ID === 'REPLACE_AFTER_DEPLOY') return null;
  try {
    return Principal.fromText(ADMIN_CANISTER_ID);
  } catch {
    return null;
  }
})();

/// Map the labels returned by `get_all_canister_cycles` to the principals
/// returned by `get_backend_principals`. The cycles list uses the Motoko
/// canister-name strings ("auth_canister", "lobby_canister", etc.); the
/// principal directory uses bare keys ("auth", "lobby", etc.).
function principalForLabel(
  label: string,
  directory: BackendPrincipals,
): Principal {
  // Strip any " (not wired)" or " (status failed)" suffix the canister
  // appends so we can match the base label.
  const base = label.replace(/ \(.+\)$/, '');
  const pick = (opt: [] | [Principal]): Principal | null =>
    opt.length === 1 ? opt[0] : null;

  let p: Principal | null = null;
  switch (base) {
    case 'auth_canister': p = pick(directory.auth); break;
    case 'lobby_canister': p = pick(directory.lobby); break;
    case 'game_canister': p = pick(directory.game); break;
    case 'subscription_canister': p = pick(directory.subscription); break;
    case 'stats_canister': p = pick(directory.stats); break;
    case 'dominus_frontend': p = pick(directory.frontend); break;
    // dominus_admin itself is not in the backend principals directory
    // (it IS the admin canister). Pull the principal from VITE_ADMIN_CANISTER_ID.
    case 'dominus_admin': p = ADMIN_PRINCIPAL; break;
    default: p = null;
  }
  return p ?? SENTINEL_PRINCIPAL;
}

/// Build a CanisterRollup with cycles populated from the cycles entry and
/// heap / memory set to 0. The OperationsPage card UI renders em-dashes
/// for the heap / memory bars when `heapBytes === 0n` and `memoryBytes === 0n`
/// (the "values not measured at this layer" path — see CanisterCard).
function buildRollup(
  entry: CanisterCyclesEntry,
  directory: BackendPrincipals,
  fetchedAt: bigint,
): CanisterRollup {
  const [label, cycles] = entry;
  const health: CanisterHealthStatus = {
    cycles,
    // v1.1 follow-up: real heap + memory require Motoko RTS bindings that
    // dfx 0.25 / moc 0.13 do not yet expose. The OperationsPage card UI
    // detects this state (both zero) and renders em-dashes rather than
    // misleading 0% bars.
    heapBytes: 0n,
    heapPercent: 0n,
    memoryBytes: 0n,
    memoryPercent: 0n,
    timestamp: fetchedAt,
  };
  return {
    label,
    principal: principalForLabel(label, directory),
    health,
  };
}

export function useCanisterHealth(identity: Identity | null): UseCanisterHealthReturn {
  const [rollups, setRollups] = useState<CanisterRollup[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef(true);

  const fetchHealth = useCallback(async () => {
    if (!identity) return;

    setIsLoading(true);
    setError(null);

    try {
      const agent = await createAdminAgent(identity);
      const adminActor = createAdminActor(agent);

      // Run both calls in parallel — backend principals are independent of
      // the cycle reads and never change at the canister level (set once
      // via init-admin.sh wiring).
      const [cyclesResult, directory] = await Promise.all([
        adminActor.get_all_canister_cycles(),
        adminActor.get_backend_principals(),
      ]);

      if (!isMountedRef.current) return;

      if ('err' in cyclesResult) {
        setError(cyclesResult.err);
        return;
      }

      const fetchedAtNs = BigInt(Date.now()) * 1_000_000n;
      const next = cyclesResult.ok.map((entry) =>
        buildRollup(entry, directory, fetchedAtNs),
      );
      setRollups(next);
      setLastFetchedAt(new Date());
    } catch (err) {
      if (!isMountedRef.current) return;
      const message = err instanceof Error ? err.message : 'Failed to fetch canister health.';
      setError(message);
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [identity]);

  const refresh = useCallback(() => {
    fetchHealth();
  }, [fetchHealth]);

  useEffect(() => {
    if (!identity) return;

    isMountedRef.current = true;
    fetchHealth();

    timerRef.current = setInterval(fetchHealth, POLL_INTERVAL_MS);

    return () => {
      isMountedRef.current = false;
      if (timerRef.current !== null) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [identity, fetchHealth]);

  // Derive treasury + total from the current rollup. Re-derived on every
  // render — cheap (max 7 entries) and keeps the source of truth in `rollups`.
  // Treasury == dominus_admin self-entry (its own ExperimentalCycles.balance);
  // Total == sum across every entry (incl. unreachable canisters which report
  // 0n by canister-side fallback, so they contribute nothing — accurate).
  let treasuryCycles: bigint | null = null;
  let totalCycles: bigint | null = null;
  if (rollups.length > 0) {
    let sum = 0n;
    for (const r of rollups) {
      sum += r.health.cycles;
      // Match the base label after stripping any " (status failed)" suffix.
      const base = r.label.replace(/ \(.+\)$/, '');
      if (base === 'dominus_admin') {
        treasuryCycles = r.health.cycles;
      }
    }
    totalCycles = sum;
  }

  return { rollups, treasuryCycles, totalCycles, isLoading, error, lastFetchedAt, refresh };
}
